import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, X, Save, CheckCircle2, Send, Lock, Unlock,
  Printer, FileText, Ban, AlertCircle, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step 4f — single page, branches by lifecycle_status:
//   open       -> skip-by-skip editor (add/edit/close/reopen/delete skips, complete)
//   completed  -> review + Send Email (or retry) + per-skip Reprint Label
//   sent       -> read-only + Resend Email + per-skip Reprint Label
//   voided     -> read-only with VOIDED banner (no-op for now)
//
// Skips inside the note follow their own open/closed flag:
//   open       -> editable item rows; Save / Close / Delete
//   closed     -> read-only rows; Print Label / Reopen (only while parent open)

const STATUS_BADGES = {
  open:      { label: 'Open',      bg: '#E6F1FB', fg: '#0C447C' },
  completed: { label: 'Completed', bg: '#FAEEDA', fg: '#854F0B' },
  sent:      { label: 'Sent',      bg: '#EAF3DE', fg: '#3B6D11' },
  voided:    { label: 'Voided',    bg: '#F1EFE8', fg: '#5F5E5A' },
};

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('en-GB'); } catch { return s; } };
const lifecycleOf = (n) => n?.lifecycle_status || (n?.status === 'sent' ? 'sent' : 'completed');

const StatusPill = ({ status }) => {
  const s = STATUS_BADGES[status] || STATUS_BADGES.open;
  return (
    <span style={{ background: s.bg, color: s.fg }} className="px-3 py-1 rounded-full text-xs font-semibold">
      {s.label}
    </span>
  );
};

// Print the label for a skip — tries the local print helper first
// (silent print direct to the Bixolon at 8765). Falls back to opening
// the label HTML in a new window for the user to print manually if
// the helper isn't running. Helper config / install instructions live
// in the print-helper/ folder of the workspace.
const HELPER_URL = 'http://localhost:8765';
const printSkipLabel = async (skipId) => {
  const labelUrl = `${API_URL}/api/operations/skips/${skipId}/label`;
  try {
    // Fetch the rendered label HTML (browser session handles auth).
    const labelResp = await fetch(labelUrl, { credentials: 'include' });
    if (!labelResp.ok) throw new Error(`Label fetch failed: ${labelResp.status}`);
    const html = await labelResp.text();
    // Hand off to local helper. Short timeout so we don't hang the
    // UI if the helper isn't responding.
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 30000);
    const helperResp = await fetch(`${HELPER_URL}/print/label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html }),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!helperResp.ok) throw new Error(`Helper failed: ${helperResp.status}`);
    return;  // Silent success — label is at the Bixolon.
  } catch (e) {
    // Helper not running, refused, errored, or timed out — fall back
    // to the existing flow (opens HTML, browser auto-fires print dialog).
    console.warn('Print helper unavailable, falling back to browser print:', e.message);
    window.open(labelUrl, '_blank');
  }
};

// ----------------------------- Skip card -----------------------------

const SkipCard = ({ skip, items, parentOpen, customerId, onSaved, onClosed, onReopened, onDeleted, onError }) => {
  // Local editable copy of the skip's items. Each row carries a stable
  // `key` so React can track row identity across add/remove.
  const initialRows = (skip.items || []).map((si, idx) => ({
    key: `r_${skip.id}_${idx}`,
    item_id: si.item_id ?? '',
    quantity: si.quantity ?? 1,
    item_code: si.item_code,
    item_description: si.item_description,
  }));
  const [rows, setRows] = useState(initialRows);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  // Refresh local state when parent re-fetches.
  useEffect(() => {
    setRows((skip.items || []).map((si, idx) => ({
      key: `r_${skip.id}_${idx}`,
      item_id: si.item_id ?? '',
      quantity: si.quantity ?? 1,
      item_code: si.item_code,
      item_description: si.item_description,
    })));
    setDirty(false);
  }, [skip.id, skip.items?.length, skip.closed]);

  const skipOpen = !skip.closed;
  // Step 8: items are always editable. Step 9 tweak: the 'post-send
  // correction' warning is only shown when the parent DN is no longer
  // open (i.e. completed / sent / voided). A closed skip on an open DN
  // is just a workflow step — label printed, customer NOT yet notified —
  // so no warning needed there.
  const editable = true;
  const postSendEdit = !parentOpen;

  const addRow = () => {
    // Step 9: empty quantity so staff have to type — avoids accidentally
    // saving the default '1' when they forget to set the real number.
    setRows([...rows, { key: `r_${skip.id}_${Date.now()}`, item_id: '', quantity: '' }]);
    setDirty(true);
  };
  const updateRow = (idx, patch) => {
    setRows(rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
    setDirty(true);
  };
  const removeRow = (idx) => {
    setRows(rows.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    // Step 8: confirm before applying a post-send correction since the
    // customer's emailed PDF will diverge from the portal record.
    if (postSendEdit && dirty) {
      if (!window.confirm(
        'This delivery note has already been sent to the customer (or the skip is closed). ' +
        'Editing now updates only the portal record — the customer\'s email still shows the original quantities. ' +
        'You can resend a corrected PDF afterwards if needed. Continue?'
      )) return;
    }
    setSaving(true);
    try {
      const valid = rows.filter(r => r.item_id && Number(r.quantity) > 0).map(r => ({
        item_id: parseInt(r.item_id, 10),
        quantity: parseInt(r.quantity, 10),
      }));
      const res = await fetch(`${API_URL}/api/operations/skips/${skip.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: valid }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      onError && onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const closeSkip = async () => {
    if (dirty) await save();
    if (rows.filter(r => r.item_id && Number(r.quantity) > 0).length === 0) {
      onError && onError('Skip has no items. Add at least one item before closing.');
      return;
    }
    setClosing(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/skips/${skip.id}/close`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Close failed (${res.status})`);
      }
      onClosed && onClosed();
      // Trigger label print after close
      printSkipLabel(skip.id);
    } catch (e) {
      onError && onError(e.message);
    } finally {
      setClosing(false);
    }
  };

  const reopenSkip = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/skips/${skip.id}/reopen`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Reopen failed (${res.status})`);
      }
      onReopened && onReopened();
    } catch (e) {
      onError && onError(e.message);
    }
  };

  const deleteSkip = async () => {
    if (!window.confirm(`Delete Skip #${skip.skip_number}? Items inside will be removed.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/operations/skips/${skip.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Delete failed (${res.status})`);
      }
      onDeleted && onDeleted();
    } catch (e) {
      onError && onError(e.message);
    }
  };

  const totalQty = rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0);

  return (
    <div className={`bg-white rounded-xl shadow-sm overflow-hidden mb-4 border ${skipOpen ? 'border-blue-200' : 'border-green-200'}`}>
      <div className={`flex items-center justify-between px-4 py-3 ${skipOpen ? 'bg-blue-50' : 'bg-green-50'}`}>
        <div className="flex items-center gap-3">
          {skipOpen ? <Unlock size={16} className="text-blue-700" /> : <Lock size={16} className="text-green-700" />}
          <span className="font-bold text-gray-800">Skip #{skip.skip_number}</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: skipOpen ? '#0C447C' : '#3B6D11', color: '#fff' }}>
            {skipOpen ? 'Open' : 'Closed'}
          </span>
          <span className="text-xs text-gray-500">{totalQty} item{totalQty === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Step 8: Save is now available in any state, not just skipOpen. */}
          {dirty && (
            <button onClick={save} disabled={saving}
              className={`flex items-center px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 ${postSendEdit ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Save size={14} className="mr-1" /> {saving ? 'Saving…' : (postSendEdit ? 'Save correction' : 'Save')}
            </button>
          )}
          {skipOpen ? (
            <>
              {parentOpen && (
                <button onClick={closeSkip} disabled={closing || saving}
                  className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
                  <Lock size={14} className="mr-1" /> {closing ? 'Closing…' : 'Close skip'}
                </button>
              )}
              {parentOpen && (
                <button onClick={deleteSkip}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                  <Trash2 size={16} />
                </button>
              )}
            </>
          ) : (
            <>
              <button onClick={() => printSkipLabel(skip.id)}
                className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                <Printer size={14} className="mr-1" /> Print label
              </button>
              {parentOpen && (
                <button onClick={reopenSkip}
                  className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Unlock size={14} className="mr-1" /> Reopen
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="p-4">
        {postSendEdit && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>
              <strong>Post-send correction.</strong> This skip is locked-stage (skip closed or delivery note already sent).
              You can still edit quantities to correct end-of-week discrepancies. The customer's emailed PDF will not change automatically;
              the portal record updates and the change is logged in the activity log.
            </span>
          </div>
        )}
        <SkipItemRowsEditor rows={rows} addRow={addRow} updateRow={updateRow} removeRow={removeRow} customerId={customerId} />
      </div>
    </div>
  );
};

// Editable row list. Each row is item drop-down + qty input + remove.
const SkipItemRowsEditor = ({ rows, addRow, updateRow, removeRow, customerId }) => {
  const [allItems, setAllItems] = useState([]);
  useEffect(() => {
    if (!customerId) return;
    // Fetch ONLY this customer's curated items (already sorted by
    // display_order on the backend). Avoids the global-items drop-down
    // bug where staff could pick something the customer doesn't even use.
    fetch(`${API_URL}/api/operations/customers/${customerId}/items`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setAllItems((d || []).filter(i => i.active !== false)))
      .catch(e => console.error('Failed loading customer items:', e));
  }, [customerId]);

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="text-sm text-gray-500 italic">No items yet. Click "Add item" to start.</div>
      )}
      {rows.map((r, idx) => (
        <div key={r.key} className="flex items-center gap-2">
          <input
            type="number" min="1" value={r.quantity}
            onChange={(e) => updateRow(idx, { quantity: e.target.value })}
            className="w-20 px-2 py-1.5 border rounded-lg text-right"
            placeholder="Qty"
          />
          <select
            value={r.item_id} onChange={(e) => updateRow(idx, { item_id: e.target.value })}
            className="flex-1 px-2 py-1.5 border rounded-lg"
          >
            <option value="">— select an item —</option>
            {allItems.map(it => (
              <option key={it.id} value={it.id}>{it.code} · {it.description}</option>
            ))}
          </select>
          <button onClick={() => removeRow(idx)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
            <X size={16} />
          </button>
        </div>
      ))}
      <button onClick={addRow}
        className="flex items-center px-3 py-1.5 text-sm border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
        <Plus size={14} className="mr-1" /> Add item
      </button>
    </div>
  );
};

const SkipItemRowsReadOnly = ({ items }) => (
  <div className="space-y-1">
    {items.length === 0 ? (
      <div className="text-sm text-gray-500 italic">(no items)</div>
    ) : items.map((si, idx) => (
      <div key={idx} className="flex items-baseline gap-3 text-sm">
        <span className="w-12 text-right font-mono font-semibold">{si.quantity}</span>
        <span className="text-gray-700">{si.item_description || si.item_code || `Item ${si.item_id}`}</span>
      </div>
    ))}
  </div>
);

// ----------------------------- Main page -----------------------------

const ViewDeliveryNote = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const showError = (msg) => { setError(msg); setInfo(''); setTimeout(() => setError(''), 6000); };
  const showInfo = (msg) => { setInfo(msg); setError(''); setTimeout(() => setInfo(''), 4000); };

  const fetchNote = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 404) { navigate('/operations/delivery-notes'); return; }
        throw new Error(`Fetch failed (${res.status})`);
      }
      const data = await res.json();
      setNote(data);
    } catch (e) {
      console.error(e);
      showError(e.message || 'Failed to load delivery note');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchNote(); }, [fetchNote]);

  const addEmptySkip = async () => {
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}/skips`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Add skip failed (${res.status})`);
      }
      await fetchNote();
    } catch (e) {
      showError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const completeNote = async () => {
    if (!window.confirm('Complete this delivery note? All skips will be locked from further edits. The note will then be ready to send to the customer.')) return;
    setCompleting(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}/complete`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Complete failed (${res.status})`);
      }
      await fetchNote();
      showInfo('Delivery note completed. Click "Send to customer" when ready.');
    } catch (e) {
      showError(e.message);
    } finally {
      setCompleting(false);
    }
  };

  const sendNote = async () => {
    if (!window.confirm(`Email this delivery note to ${note.customer_name}? The PDF will be attached.`)) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}/send`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Send failed (${res.status})`);
      }
      await fetchNote();
      showInfo('Email sent successfully.');
    } catch (e) {
      showError(e.message);
    } finally {
      setSending(false);
    }
  };

  const deleteNote = async () => {
    if (!window.confirm('Delete this OPEN delivery note? All skips and items inside will be removed. This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Delete failed (${res.status})`);
      }
      navigate('/operations/delivery-notes');
    } catch (e) {
      showError(e.message);
      setDeleting(false);
    }
  };

  const voidNote = async () => {
    if (!window.confirm('Void this delivery note? It will stay in the system for audit but cannot be reactivated. Use this for delivery notes that were sent in error.')) return;
    setVoiding(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes/${id}/void`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Void failed (${res.status})`);
      }
      await fetchNote();
      showInfo('Delivery note voided.');
    } catch (e) {
      showError(e.message);
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-lg text-gray-600">Loading…</div></div>;
  }
  if (!note) return null;

  const lifecycle = lifecycleOf(note);
  const isOpen = lifecycle === 'open';
  const isCompleted = lifecycle === 'completed';
  const isSent = lifecycle === 'sent';
  const skips = note.skips || [];
  const allClosed = skips.length > 0 && skips.every(s => s.closed);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/operations/delivery-notes')} className="p-2 hover:bg-gray-100 rounded-lg mt-1">
            <ArrowLeft size={22} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
                DN #{note.note_number}
              </h1>
              <StatusPill status={lifecycle} />
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {note.customer_name} <span className="text-gray-400">({note.customer_code})</span>
              <span className="mx-2">·</span>
              Processed {fmtDate(note.request_date)}
              <span className="mx-2">·</span>
              Delivered {fmtDate(note.delivery_date)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <a href={`${API_URL}/api/operations/delivery-notes/${id}/pdf`} target="_blank" rel="noreferrer"
            className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            <FileText size={14} className="mr-1" /> Full PDF
          </a>
          {isOpen && (
            <button onClick={deleteNote} disabled={deleting}
              className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50">
              <Trash2 size={14} className="mr-1" /> Delete
            </button>
          )}
          {isCompleted && (
            <button onClick={sendNote} disabled={sending}
              className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
              <Send size={14} className="mr-1" /> {sending ? 'Sending…' : 'Send to customer'}
            </button>
          )}
          {isSent && (
            <button onClick={sendNote} disabled={sending}
              className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <Mail size={14} className="mr-1" /> {sending ? 'Resending…' : 'Resend email'}
            </button>
          )}
          {(isCompleted || isSent) && (
            <button onClick={voidNote} disabled={voiding}
              className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50">
              <Ban size={14} className="mr-1" /> {voiding ? 'Voiding…' : 'Void'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}
      {info && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <CheckCircle2 size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-green-700">{info}</span>
        </div>
      )}

      {/* Skips */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
            Skips ({skips.length})
          </h2>
          {isOpen && (
            <button onClick={addEmptySkip} disabled={adding}
              className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
              <Plus size={14} className="mr-1" /> {adding ? 'Adding…' : 'Add skip'}
            </button>
          )}
        </div>
        {skips.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500 italic">
            {isOpen ? 'No skips yet. Click "Add skip" to start.' : 'No skips on this delivery note.'}
          </div>
        ) : (
          skips.map(skip => (
            <SkipCard key={skip.id} skip={skip} parentOpen={isOpen}
              customerId={note.customer_id}
              onSaved={fetchNote} onClosed={fetchNote} onReopened={fetchNote} onDeleted={fetchNote}
              onError={showError} />
          ))
        )}
      </div>

      {/* Complete & Send action (only when open) */}
      {isOpen && (
        <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border-l-4 border-[#1F2D3D]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-bold text-gray-800">Ready to finish?</div>
              <div className="text-sm text-gray-600 mt-1">
                {skips.length === 0
                  ? 'Add at least one skip first.'
                  : !allClosed
                    ? 'Close every skip on the page above, then complete the note.'
                    : `All ${skips.length} skip${skips.length === 1 ? '' : 's'} closed. Click Complete to lock the note, then send.`}
              </div>
            </div>
            <button onClick={completeNote}
              disabled={completing || !allClosed || skips.length === 0}
              className="flex items-center px-5 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50 disabled:cursor-not-allowed">
              <CheckCircle2 size={16} className="mr-2" /> {completing ? 'Completing…' : 'Complete delivery note'}
            </button>
          </div>
        </div>
      )}

      {/* Activity log */}
      {note.activity_log && note.activity_log.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-sm font-bold text-gray-700 mb-3" style={{ fontFamily: '"Cinzel", serif' }}>
            Activity
          </h2>
          <ul className="space-y-1.5">
            {note.activity_log.map((entry, idx) => (
              <li key={idx} className="text-xs text-gray-600">
                <span className="text-gray-400 font-mono mr-2">{fmtDate(entry.timestamp)}</span>
                <span className="font-semibold">{entry.action}</span>
                <span className="text-gray-400 mx-1">·</span>
                <span>{entry.details}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ViewDeliveryNote;
